import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { landmineNames } from '@mm/constants/landmine-names';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { useSalesForceCRUDActions } from '@mm/state/actions/SalesForceCRUDActions';
import { selectSalesForceConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';

export const useConnectSalesForce = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const accounts = useSelector(
    (state: StoreState) => state.salesForce.accounts,
  );
  const salesForceConnections = useSelector(selectSalesForceConnections);
  const salesForceConnection = salesForceConnections?.[0];

  const [oauthUrl, setOauthUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const { getSalesForceOauthUrl, getAccounts } = useSalesForceCRUDActions();
  const { openNewBillingModal2023 } = useModalActions();
  const { create, retrieveById } = useConnectionCRUDActions();
  const { retrieveAll } = useConnectionCRUDActions();

  const fetchOAuthUrl = useCallback(async () => {
    const link = await getSalesForceOauthUrl(
      window.location.href,
      facebookPageId,
    );

    if (link) {
      setOauthUrl(link);
    }
    return link;
  }, [facebookPageId, getSalesForceOauthUrl]);

  const connectSalesForce = useCallback(async () => {
    if (!hasNativeIntegrationsFeature) {
      openNewBillingModal2023({
        landmineName: landmineNames.SALESFORCE_UPGRADE,
        onSuccessBilling: connectSalesForce,
      });
      return;
    }
    setBusy(true);
    const link = await getSalesForceOauthUrl(
      window.location.href,
      facebookPageId,
    );

    if (link) {
      setOauthUrl(link);
      window.open(link, '_self');
    }
    setBusy(false);
  }, [
    hasNativeIntegrationsFeature,
    facebookPageId,
    getSalesForceOauthUrl,
    openNewBillingModal2023,
  ]);

  const retrieveSalesForceAccounts = () => {
    getAccounts(facebookPageId);
  };

  const getSaleForceConnections = async () => {
    const { connections } =
      (await retrieveAll(facebookPageId, {
        type: 'salesforce',
        pageSize: 200,
      })) || {};

    if (
      accounts?.length &&
      !connections?.length &&
      hasNativeIntegrationsFeature
    ) {
      await create(facebookPageId, {
        name: 'SalesForce Connection',
        connectionKind: 'salesforce',
        salesforceParams: { accessTokenId: accounts[0].id },
      });
    }
  };

  const refreshConnections = async () => {
    if (salesForceConnection) {
      await retrieveById(facebookPageId, salesForceConnection.id);
    }
  };

  return {
    oauthUrl,
    connectSalesForce,
    retrieveSalesForceAccounts,
    busy,
    fetchOAuthUrl,
    refreshConnections,
    salesForceConnection,
    getSaleForceConnections,
  };
};
